import { SUPPORTED_POS_TYPES } from 'constants/pos';
import type { ConnectedPos, PosConfigPreset, PosResource } from 'types/pos';

/** The POS fields the dashboard reads off a Cuboh company. */
type PosCompany = {
  /** Cuboh company id — the path segment for menu-management calls. */
  id?: number | string;
  pos_active?: boolean;
  pos_resource?: PosResource | null;
};

/** The slice of `scopeDetails` the POS checks read. */
export type PosScopeDetails = {
  /**
   * The Cuboh company for the selected location, resolved by HeadquarterContext —
   * `cuboh-{id}` URLs have no ChowNow location to match against, so this can't be
   * re-derived from `location.id` here.
   */
  cubohCompany?: PosCompany;
};

/** The POS on the Cuboh company whatever its type, or null when none is active. */
export const getCubohPos = ({ cubohCompany }: PosScopeDetails): ConnectedPos | null => {
  const displayName = cubohCompany?.pos_resource?.display_name;
  const type = cubohCompany?.pos_resource?.type;

  if (!cubohCompany?.pos_active || !displayName || !type) return null;

  return { type, display_name: displayName };
};

/**
 * The connected POS for the selected location, or null when there's nothing to render
 */
export const getConnectedPos = (scopeDetails: PosScopeDetails): ConnectedPos | null => {
  const pos = getCubohPos(scopeDetails);

  return pos && SUPPORTED_POS_TYPES.includes(pos.type) ? pos : null;
};

export const canSeePosMenuSettings = (scopeDetails: PosScopeDetails = {}): boolean =>
  !!getConnectedPos(scopeDetails);

/**
 * Shown when the company's flags match no preset. BE will guarantee one active config in a
 * later release; until then this is reachable — mapping-mode companies match nothing.
 */
const NO_ACTIVE_PRESET_MODE = 'N/A';

/** Ascending by ordinal; presets missing an ordinal keep their original position. */
function compareByOrdinal(a: PosConfigPreset, b: PosConfigPreset): number {
  if (a.ordinal == null || b.ordinal == null) return 0;

  return a.ordinal - b.ordinal;
}

/** The sync presets the location's POS allows, sorted for the POS Settings drawer. */
export function getPosConfigPresets({ cubohCompany }: PosScopeDetails): PosConfigPreset[] {
  return [...(cubohCompany?.pos_resource?.allowed_configs ?? [])].sort(compareByOrdinal);
}

/** Display copy for the location's sync mode — the active preset's name verbatim, or 'N/A'. */
export function getSyncMode(scopeDetails: PosScopeDetails): string {
  const activeName = getPosConfigPresets(scopeDetails).find(({ active }) => active)?.name;

  return activeName ?? NO_ACTIVE_PRESET_MODE;
}
